import { ApolloClient, ApolloQueryResult, NormalizedCacheObject, useReactiveVar } from "@apollo/client";
import { GetPageDocument, GetPageQuery, GetPageQueryVariables } from "./PageService.hooks";
import { pageVar } from "../../graphql/state";
import { PageType } from "../../types/Page";

let _refetch: ((pageId: number) => Promise<ApolloQueryResult<GetPageQuery>>) | null = null;

export const fetchPage = async (
    pageId: number,
    apolloClient: ApolloClient<NormalizedCacheObject>
): Promise<PageType> => {
    const { data } = await apolloClient.query<GetPageQuery, GetPageQueryVariables>({
        query: GetPageDocument,
        variables: { id: pageId },
    });
    pageVar(data.page);
    return data.page;
};

export const pageService = {
  getPage: () => pageVar(),
  setPage: (page: PageType) => {
    if (!page) return;
    pageVar(page);
  },
  updateCurrentPageTitle: (title: string) => {
    const currentPage = pageVar();
    if (!currentPage?.id) return;

    const currentPageId = currentPage.id;
    const parentPage = currentPage.parentPage
      ? {
          ...currentPage.parentPage,
          subPages: currentPage.parentPage.subPages.map((subPage) =>
            subPage.id === currentPageId ? {...subPage, title} : subPage,
          ),
        }
      : currentPage.parentPage;

    pageVar({
      ...currentPage,
      title,
      parentPage,
    });
  },
  fetch: async (pageId: number, apolloClient: ApolloClient<NormalizedCacheObject>): Promise<PageType> => {
    _refetch = async (id: number) => apolloClient.query<GetPageQuery, GetPageQueryVariables>({
        query: GetPageDocument,
        variables: { id },
        fetchPolicy: 'network-only',
    });
    const { data } = await _refetch(pageId);
    pageVar(data.page);
    return data.page;
  },
  refetch: async (pageId: number): Promise<PageType | null> => {
    if (!_refetch) return null;
    const { data } = await _refetch(pageId);
    pageVar(data.page);
    return data.page;
  },
};

export const usePageService = () => {
  const page = useReactiveVar(pageVar);
  return { page };
};
